/* Atrium domain model — the app.atrium.* schema, projected into view models.
   Everything here is backed by real homeserver state. No mock shapes. */

export const PRESENCE_MODES = ['open', 'cowork', 'meeting', 'deep', 'break', 'away'] as const
export type PresenceMode = (typeof PRESENCE_MODES)[number]

export interface ModeMeta {
  id: PresenceMode
  label: string
  hint: string
  color: string
  glow: string
  icon: string
}

export const MODES: Record<PresenceMode, ModeMeta> = {
  open: { id: 'open', label: 'Open', hint: 'Grab me anytime', color: 'var(--p-open)', glow: 'var(--p-open-glow)', icon: 'door' },
  cowork: { id: 'cowork', label: 'Co-working', hint: 'Here, heads-down — knock', color: 'var(--p-cowork)', glow: 'var(--p-cowork-glow)', icon: 'headphones' },
  meeting: { id: 'meeting', label: 'In a meeting', hint: 'In a room right now', color: 'var(--p-meeting)', glow: 'var(--p-meeting-glow)', icon: 'users' },
  deep: { id: 'deep', label: 'Deep work', hint: 'DND — knocks queue', color: 'var(--p-deep)', glow: 'var(--p-deep-glow)', icon: 'moon' },
  break: { id: 'break', label: 'Break', hint: 'Back shortly', color: 'var(--p-break)', glow: 'var(--p-break-glow)', icon: 'coffee' },
  away: { id: 'away', label: 'Away', hint: 'Not around', color: 'var(--p-away)', glow: 'transparent', icon: 'clock' },
}

export function modeMeta(id: string | undefined): ModeMeta {
  return MODES[(id as PresenceMode)] ?? MODES.away
}

export function isPresenceMode(v: string): v is PresenceMode {
  return (PRESENCE_MODES as readonly string[]).includes(v)
}

/** A live calendar meeting, projected from app.atrium.calendar.member (§10.4):
 *  what the person's Google Calendar says they're in right now. Summary is
 *  absent for private events. */
export interface PersonMeeting {
  summary?: string
  start?: number // ms
  end?: number // ms
  meet?: boolean // has a Google Meet link
  slug?: string // bound Atrium room, when the meeting lives on the floor
}

/** A real person on the floor: an org-Space member, enriched by presence projection. */
export interface Person {
  id: string // MXID
  name: string
  short: string
  color: string
  /** Custom avatar image (mxc:// URI from the Matrix profile); initials otherwise. */
  avatarUrl?: string
  mode: PresenceMode
  now: string
  accessory?: string
  you: boolean
  guest?: boolean
  server?: string
  /** Google Calendar linked (calendar badge shows when in a meeting). */
  calendarConnected?: boolean
  /** Their linked Google address — known once they connect the calendar. */
  email?: string
  meeting?: PersonMeeting
}

export type RoomKind =
  | 'project' | 'stage' | 'focus' | 'watercooler' | 'meeting' | 'office-hours' | 'war' | 'huddle'

/** A team: a private Space whose membership IS the team (org-Space registry). */
export interface TeamInfo {
  id: string
  name: string
  spaceId: string
}

/** A room on the floor: from app.atrium.floor.layout, overlaid with live state. */
export interface Room {
  id: string // floor slug
  roomId: string // matrix room id
  name: string
  kind: RoomKind
  hood: string
  /** Team binding: members walk in, everyone else knocks (knock_restricted). */
  team?: string
  x: number
  y: number
  w: number
  h: number
  accent: string
  external: boolean
  incident: boolean
  // live, derived
  occupants: string[] // MXIDs
  heat: number // 0..1
  sharers: string[] // MXIDs sharing a screen
  cams: number
  boardOpen: boolean
  boardBy?: string
  tasksOpen: boolean
  tasksBy?: string
  activity: string
  // live, kind-specific (read from the room's app.atrium.* state)
  pomo?: { phase: 'focus' | 'break'; endsAt: number }
  incidentInfo?: { title?: string; since?: number }
  doorsOpen?: boolean
  queueLen?: number
}

export interface Hood {
  id: string
  label: string
  x: number
  y: number
  w: number
}

export interface RoomTypeMeta {
  icon: string
  label: string
  /** One line of what makes this kind of place different. */
  hint: string
}

export const ROOM_TYPES: Record<string, RoomTypeMeta> = {
  project: { icon: 'board', label: 'Project room', hint: 'Board-first — the work lives here' },
  stage: { icon: 'radio', label: 'Stage', hint: 'One presents, many listen — raise a hand to speak' },
  focus: { icon: 'pomo', label: 'Focus room', hint: 'Quiet co-working — mics off, shared pomodoro' },
  watercooler: { icon: 'coffee', label: 'Watercooler', hint: 'Wander around — you hear the people near you' },
  meeting: { icon: 'users', label: 'Meeting room', hint: 'Agenda on the side — keep it on track' },
  'office-hours': { icon: 'door', label: 'Office hours', hint: 'Take a number — the host works the queue' },
  war: { icon: 'flame', label: 'War room', hint: 'Declare it, log it, resolve it' },
  huddle: { icon: 'bolt', label: 'Huddle', hint: 'Get in, decide, get out' },
}

export function roomTypeMeta(kind: string): RoomTypeMeta {
  return ROOM_TYPES[kind] ?? ROOM_TYPES.project
}

/* ---- kind-specific room state (app.atrium.* state events, view models) ---- */

/** app.atrium.timer ('' key) — the shared Pomodoro. The state IS the schedule:
 *  every client derives the countdown from ends_at, no server tick. */
export interface TimerState {
  running: boolean
  phase: 'focus' | 'break'
  ends_at: number // ms epoch
  focus_min: number
  break_min: number
  by?: string
  ts?: number
}

/** app.atrium.officehours ('' key) — are the doors open, and whose hours are they. */
export interface OfficeHoursState {
  open: boolean
  host?: string
  note?: string
  ts?: number
}

/** app.atrium.queue (state_key = the visitor's own MXID — the server enforces
 *  you can only take/leave your own spot). */
export interface QueueEntry {
  user: string
  topic?: string
  ts: number
}

/** app.atrium.incident ('' key) — war-room lifecycle. */
export interface IncidentState {
  active: boolean
  title?: string
  severity?: 'minor' | 'major' | 'critical'
  by?: string
  since?: number // ms
  resolved_at?: number // ms
}

/** One row of app.atrium.agenda ('' key, items array). */
export interface AgendaItem {
  id: string
  text: string
  done: boolean
  by?: string
}

export type SoundChoice = 'rain' | 'noise' | 'off'

/** app.atrium.presence (account data). */
export interface PresenceDoc {
  mode: PresenceMode
  manual: boolean
  now: string
  since: number
  source: 'manual' | 'calendar' | 'inactivity' | 'focus' | 'rtc'
  accessory?: string
  expires?: number | null
}

/** A chat message in a room timeline (view model). */
export interface ChatMessage {
  id: string
  sender: string
  body: string
  ts: number
  pending?: boolean
  failed?: boolean
  txn?: string | null
  /** 'summary' = an app.atrium.readout meeting summary, rendered as a card.
      'file' = an m.file/m.image/m.video/m.audio attachment event. */
  kind?: 'summary' | 'file'
  summary?: MeetingReadout
  /** Attachment carried by a media msgtype (url is an mxc://). */
  file?: { url: string; name: string; mime?: string; size?: number }
  /** Event id this message replies to (m.in_reply_to) — rendered as a quote block. */
  replyTo?: string
}

/** One reaction chip under a message: everyone who picked the same key,
    aggregated. The key is a unicode emoji, an ":shortcode:" custom emote
    (image in `url`), or an https GIF URL. */
export interface MessageReaction {
  key: string
  count: number
  /** My own reaction event id when I'm among the senders — what gets redacted on toggle-off. */
  mine: string | null
  senders: string[]
  /** mxc:// image for custom-emote reactions (travels in the reaction event). */
  url?: string
}

/** Content of an app.atrium.readout timeline event (kind=meeting_summary):
    the post-meeting summary the memory layer posts into the room (§11). */
export interface MeetingReadout {
  kind: string
  v?: number
  meeting_id: number
  session_id: string
  summary_md: string
  topics?: string[]
  participants?: string[]
  started_at?: number
  ended_at?: number
  partial?: boolean
  suggestions?: { knowledge: number; tasks: number }
  replaces?: string
}

export type SignalKind = 'knock' | 'roomknock' | 'grab' | 'mention' | 'enter'

export interface InboxItem {
  id: string
  kind: SignalKind
  from?: string
  fromName?: string
  roomId?: string
  slug?: string | null
  roomName?: string
  text?: string
  ts: number
  read: boolean
  accepted?: boolean
  /** Navigate to `from`'s LIVE room when opened (slug is just the fallback). */
  follow?: boolean
}

/** Where an @-mention of you happened. Chat and tickets are kept apart in the
 *  persistent timeline (see the mentions store + MentionsView). */
export type MentionSource = 'chat' | 'ticket'

/** Why a timeline item landed in your Mentions view. `'mention'` is the classic
 *  @-mention; `'sub'` is a task-subscription update (you're subscribed because
 *  you authored, were assigned, or were mentioned). Absent ⇒ `'mention'`, so
 *  items stored before this feature render unchanged. */
export type MentionKind = 'mention' | 'sub'

/** A subscription update's verb (kind `'sub'` only) — drives the row/toast copy
 *  in MentionsView. */
export type MentionAction = 'assigned' | 'commented' | 'moved' | 'updated' | 'created' | 'transferred'

/** One item in the durable Mentions/activity timeline. `id` is the source event
 *  id (the dedupe key). The chat fields locate a room message; the ticket fields
 *  locate a board ticket. */
export interface MentionItem {
  id: string
  source: MentionSource
  from: string
  fromName: string
  text: string
  ts: number
  read: boolean
  kind?: MentionKind // undefined ⇒ 'mention'
  action?: MentionAction // for kind 'sub'
  // chat
  roomId?: string
  slug?: string | null
  roomName?: string
  // ticket
  board?: string
  ticket?: string
  ticketTitle?: string
}
